<template>
  <div
    class="icon"
    :data-icon="iconName"
    :class="{
      'icon-black': iconColor === 'black',
      'icon-white': iconColor === 'white',
      'icon-orange': iconColor === 'orange',
      'icon-blue': iconColor === 'blue',
      'icon-green': iconColor === 'green',
      'icon-dark-green': iconColor === 'darkGreen',
      'icon-gray': iconColor === 'gray',
      'icon-red': iconColor === 'red',
      'icon-aquamarin': iconColor === 'aquamarin',
      'is-without-hover': isWithoutHover,
    }"
    @click="iconClick"
  >
    <i :style="iconMaskStyle" />
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue'
  import { defineComponent } from 'vue'
  import { IconColorsI } from '@/typescript/types/ui-components/BaseIconI'

  export default defineComponent({
    name: 'BaseIcon',
    props: {
      iconName: {
        type: String,
        default: 'close-circle.svg',
      },
      iconColor: {
        type: String as PropType<IconColorsI>,
        default: '',
      },
      iconColorHash: {
        type: String,
        default: '',
      },
      width: {
        type: String,
        default: '20px',
      },
      height: {
        type: String,
        default: '20px',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      isWithoutHover: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['iconClick'],
    computed: {
      iconMaskStyle() {
        // Quote the url() so inlined SVG data: URIs (Vite inlines small SVGs) — which
        // contain single quotes/spaces — stay valid CSS. Built here (not inline in the
        // template) to avoid double-quote conflicts with the :style attribute delimiter.
        const maskUrl = `url("${this.$baseScript.getIconUrl(this.iconName)}")`
        return {
          '-webkit-mask-image': maskUrl,
          'mask-image': maskUrl,
          'background-color': this.iconColorHash,
        }
      },
      getWidth() {
        return this.width
      },
      getHeight() {
        return this.height
      },
    },
    methods: {
      iconClick() {
        if (!this.disabled) {
          this.$emit('iconClick')
        }
      },
    },
  })
</script>

<style scoped lang="scss">
  .icon {
    width: v-bind(getWidth);
    height: v-bind(getHeight);

    i {
      display: block;
      -webkit-mask-size: contain;
      mask-size: contain;
      -webkit-mask-repeat: no-repeat;
      mask-repeat: no-repeat;
      width: v-bind(getWidth);
      height: v-bind(getHeight);
    }

    &.icon-black i {
      background-color: black;
      &:hover {
        background-color: #292929;
      }
    }

    &.icon-green i {
      background-color: #4fb73d;
      &:hover {
        background-color: #4fb73d;
      }
    }
    &.icon-dark-green i {
      background-color: #118938;
      &:hover {
        background-color: #118938;
      }
    }
    &.icon-gray i {
      background-color: #b4b4b4;
      &:hover {
        background-color: #979797;
      }
    }

    &.icon-white i {
      background-color: white;
      &:hover {
        background-color: #c4c4c4;
      }
    }

    &.icon-orange i {
      background-color: #d9692c;
    }

    &.icon-orange:not(.is-without-hover) i:hover {
      background-color: #ffb232;
    }

    &.icon-blue i {
      background-color: #7bd1d8;
    }

    &.icon-blue:not(.is-without-hover) i:hover {
      background-color: #7bd1d889;
    }

    &.icon-red i {
      background-color: #ff4b6e;
      &:hover {
        background-color: #e73345fc;
      }
    }
    &.icon-aquamarin i {
      background-color: #07a287;
      &:hover {
        background-color: #047c67;
      }
    }
  }
</style>
